import { type ClientTool } from '@matrix-forge/ui/hooks/useClientTools';

export interface UserInputField {
  name: string;
  type: 'string' | 'boolean' | 'number' | 'password';
  label: string;
  description?: string;
  required?: boolean;
  default?: any;
}

export interface RequestUserInputArgs {
  title: string;
  description?: string;
  fields: UserInputField[];
}

// Global map to track pending input requests
const pendingInputRequests = new Map<
  string,
  {
    resolve: (value: any) => void;
    reject: (error: any) => void;
    args: RequestUserInputArgs;
  }
>();

/**
 * Get all pending input requests.
 * Used by the UI to render active forms.
 */
export function getPendingInputRequests() {
  return Array.from(pendingInputRequests.entries()).map(([id, data]) => ({
    id,
    ...data.args,
  }));
}

/**
 * Submit a user input response.
 * Called when the user fills out and submits the form.
 */
export function submitUserInput(requestId: string, data: Record<string, any>) {
  const request = pendingInputRequests.get(requestId);
  if (request) {
    request.resolve({ data });
    pendingInputRequests.delete(requestId);
  }
}

/**
 * Cancel a user input request.
 */
export function cancelUserInput(requestId: string, reason?: string) {
  const request = pendingInputRequests.get(requestId);
  if (request) {
    request.reject(new Error(reason || 'User cancelled input request'));
    pendingInputRequests.delete(requestId);
  }
}

/**
 * Client tool that allows agents to request structured input from users.
 *
 * When called, this tool displays a form to the user and waits for their response.
 * The tool call blocks until the user submits the form.
 */
export const requestUserInputTool: ClientTool = {
  name: 'request_user_input',
  description: `Request structured input from the user via a form.

Use this tool when you need information from the user to proceed. You can define
custom form fields with types, labels, and validation rules.

Example use cases:
- Request approval before taking an action
- Collect additional information needed to proceed
- Ask the user to choose between options
- Get confirmation with optional comments
- Collect sensitive credentials (use type: "password" for tokens, API keys, passwords)

The form will appear in the UI and the tool will wait for the user to respond.
Fields with type "password" render as masked inputs so credentials are not visible.`,
  parameters: {
    type: 'object',
    required: ['title', 'fields'],
    properties: {
      title: {
        type: 'string',
        description: 'Title shown at the top of the form (e.g., "Approval Required")',
      },
      description: {
        type: 'string',
        description: 'Optional description explaining what input is needed',
      },
      fields: {
        type: 'array',
        description: 'List of form fields to present to the user',
        items: {
          type: 'object',
          required: ['name', 'type', 'label'],
          properties: {
            name: {
              type: 'string',
              description: 'Field identifier (used as key in response object)',
            },
            type: {
              type: 'string',
              enum: ['string', 'boolean', 'number', 'password'],
              description:
                'Field type determining the input widget. Use "password" for sensitive values like tokens, API keys, and passwords — the input will be masked and the value will not appear in chat history.',
            },
            label: {
              type: 'string',
              description: 'User-facing label for the field',
            },
            description: {
              type: 'string',
              description: 'Optional help text shown below the field',
            },
            required: {
              type: 'boolean',
              description: 'Whether this field must be filled out',
            },
            default: {
              type: ['string', 'boolean', 'number'],
              description: 'Default value for the field',
            },
          },
        },
      },
    },
  },
  handler: async (args: RequestUserInputArgs) => {
    // Generate unique ID for this request
    const requestId = `input_${Date.now()}_${Math.random().toString(36).substring(7)}`;

    console.log('[requestUserInputTool] Creating input request:', {
      requestId,
      title: args.title,
      fieldCount: args.fields?.length,
    });

    // Create a promise that will be resolved when the user submits the form
    const responsePromise = new Promise<any>((resolve, reject) => {
      pendingInputRequests.set(requestId, {
        resolve,
        reject,
        args,
      });

      // Set a timeout to auto-cancel after 5 minutes
      setTimeout(
        () => {
          if (pendingInputRequests.has(requestId)) {
            reject(new Error('Input request timed out after 5 minutes'));
            pendingInputRequests.delete(requestId);
          }
        },
        5 * 60 * 1000
      );
    });

    try {
      const response = await responsePromise;
      console.log('[requestUserInputTool] User submitted input:', {
        requestId,
        data: response.data,
      });
      return { result: response.data };
    } catch (error) {
      console.error('[requestUserInputTool] Error or cancellation:', error);
      return {
        error: {
          message: error instanceof Error ? error.message : 'Failed to get user input',
        },
      };
    }
  },
};
